import React, { useEffect, useState } from 'react';
import { FaPaperclip } from 'react-icons/fa';
import apiService from '../services/apiService';
import FlashMessage from './FlashMessage';
import PaymentAttachments from './PaymentAttachments';
import {
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
} from '../styles/SubscriptionFormModalStyles';
import {
  BillsTableWrapper,
  BillsTableEl,
  BillsTableHeader,
  BillsTableRow,
  BillsTableData,
} from '../styles/BillsTableStyles';
import { FormRow, Label, SubmitButton } from '../styles/ComponentStyles';
import { BillNumberInput, BillDateInput } from '../styles/BillsFormStyles';
import { sortByDate } from '../utils/sortByDate';
import {
  LINKED_NOTICE,
  UNLINKED_NOTICE,
  deletePaymentWarning,
  linkedParentIds,
  readPaymentLinks,
} from '../utils/paymentLink';
import { attachmentCount, readPaymentAttachments } from '../utils/paymentAttachments';
import { LinkPaymentPrompt, PaymentLinkCell } from './PaymentLinkControls';

const rowBtnStyle = (color) => ({
  background: 'none',
  border: 'none',
  color,
  cursor: 'pointer',
});

const SubscriptionPaymentsModal = ({ subscription, onClose, onRefresh }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flashMessage, setFlashMessage] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  // Linked payments: the link picker (opened after an add or from a row) and
  // the inline unlink confirm. A payment can hold several links, so the pending
  // confirm has to name both the payment and the single link being removed.
  const [linkPromptPaymentId, setLinkPromptPaymentId] = useState(null);
  const [pendingUnlink, setPendingUnlink] = useState(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  // Receipts hang off the payment, and the attachment endpoints are
  // payment-scoped (/bills/payments/{id}/attachments) rather than bill-scoped —
  // which is exactly why the same receipt can be added from either side and seen
  // from both. One panel open at a time, below the table.
  const [attachmentsForId, setAttachmentsForId] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [newPayment, setNewPayment] = useState({
    payment_date: '',
    payment_amount: '',
    payment_charge: '',
    payment_method: '',
    payment_type: 'Principal',
    remarks: '',
    source: 'Frontend',
  });

  const setFlash = (msg) => {
    setFlashMessage(msg);
    setTimeout(() => setFlashMessage(''), 3000);
  };

  // Returns the list as well as storing it, so callers can inspect what came
  // back (the add endpoint answers with a message only — no payment id).
  const fetchPayments = async () => {
    if (!subscription?._id) return [];
    setLoading(true);
    try {
      const res = await apiService.getSubscriptionPayments(subscription._id);
      const raw = res?.payments || res?.data?.payments || [];
      const list = Array.isArray(raw) ? raw : [];
      setPayments(list);
      return list;
    } catch (e) {
      console.error(e);
      setPayments([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [subscription?._id]);

  const resetForm = () => {
    setEditingId(null);
    setNewPayment({
      payment_date: '',
      payment_amount: '',
      payment_charge: '',
      payment_method: '',
      payment_type: 'Principal',
      remarks: '',
      source: 'Frontend',
    });
  };

  const handleEdit = (p) => {
    setEditingId(p.id);
    setPendingUnlink(null);
    setLinkPromptPaymentId(null);
    setNewPayment({
      payment_date: formatPaymentDate(p.payment_date),
      payment_amount: p.payment_amount ?? '',
      payment_charge: p.payment_charge ?? '',
      payment_method: p.payment_method ?? '',
      payment_type: p.payment_type || 'Principal',
      remarks: p.remarks ?? '',
      source: p.source || 'Frontend',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPayment.payment_date || !newPayment.payment_amount || !newPayment.payment_method) {
      setFlash('Please fill Payment Date, Amount, and Method.');
      return;
    }
    try {
      if (editingId) {
        // PaymentDataEdited — no biller; amounts as strings
        const payload = {
          payment_date: newPayment.payment_date,
          payment_amount: String(newPayment.payment_amount),
          payment_charge: newPayment.payment_charge ? String(newPayment.payment_charge) : '',
          payment_method: newPayment.payment_method,
          payment_type: newPayment.payment_type || 'Principal',
          remarks: newPayment.remarks || '',
          source: newPayment.source || 'Frontend',
        };
        await apiService.updateSubscriptionPayment(subscription._id, editingId, payload);
        setFlash('Payment updated.');
        resetForm();
        await fetchPayments();
        if (onRefresh) onRefresh();
      } else {
        const payload = {
          biller: subscription.biller || '',
          payment_date: newPayment.payment_date,
          payment_amount: String(newPayment.payment_amount),
          payment_charge: newPayment.payment_charge ? String(newPayment.payment_charge) : '',
          payment_method: newPayment.payment_method,
          payment_type: newPayment.payment_type || 'Principal',
          remarks: newPayment.remarks || '',
          source: newPayment.source || 'Frontend',
        };
        // Snapshot the ids first: the payment just created is the one that
        // wasn't in the list a moment ago.
        const beforeIds = new Set(payments.map((p) => p?.id).filter(Boolean));
        await apiService.postSubscriptionPayment(subscription._id, payload);
        setFlash('Payment added.');
        resetForm();
        const list = await fetchPayments();
        if (onRefresh) onRefresh();

        const created = list.find((p) => p?.id && !beforeIds.has(p.id));
        setPendingUnlink(null);
        setLinkPromptPaymentId(created?.id || null);
      }
    } catch (err) {
      setFlash(err?.response?.data?.detail || err?.message || 'Failed to save payment.');
    }
  };

  // A subscription payment can only ever be linked to a bill.
  const loadBillOptions = async () => {
    const response = await apiService.getBills(1, 500);
    const data = response?.data || response;
    const items = Array.isArray(data?.items) ? data.items : [];
    return items
      .filter((b) => b?._id)
      .map((b) => ({
        id: b._id,
        label: b.due_date ? `${b.biller || 'Untitled bill'} — due ${b.due_date}` : b.biller || 'Untitled bill',
      }));
  };

  // Adds ONE link and leaves the picker open — it offers "Link another" so the
  // same payment can settle several bills in one pass.
  const handleLinkPayment = async (billId) => {
    await apiService.linkSubscriptionPayment(subscription._id, linkPromptPaymentId, {
      type: 'bill',
      id: billId,
    });
    setFlash(LINKED_NOTICE);
    await fetchPayments();
    if (onRefresh) onRefresh();
  };

  // Removes just the one link. The payment itself, and its other links, stay.
  const handleUnlink = async (paymentId, linkId) => {
    setUnlinkBusy(true);
    try {
      await apiService.unlinkSubscriptionPayment(subscription._id, paymentId, linkId);
      setPendingUnlink(null);
      setFlash(UNLINKED_NOTICE);
      await fetchPayments();
      if (onRefresh) onRefresh();
    } catch (err) {
      setFlash(err?.response?.data?.detail || err?.message || 'Failed to unlink payment.');
    } finally {
      setUnlinkBusy(false);
    }
  };

  const handleDelete = async (paymentId) => {
    setDeleteBusy(true);
    try {
      await apiService.deleteSubscriptionPayment(subscription._id, paymentId);
      setFlash('Payment deleted.');
      setPendingUnlink(null);
      if (linkPromptPaymentId === paymentId) setLinkPromptPaymentId(null);
      if (attachmentsForId === paymentId) setAttachmentsForId(null);
      await fetchPayments();
      if (onRefresh) onRefresh();
    } catch (err) {
      setFlash(err?.response?.data?.detail || 'Failed to delete.');
    } finally {
      setDeleteBusy(false);
      setPendingDeleteId(null);
    }
  };

  // The payments list owns the attachments, so an upload or a removal is folded
  // back into it rather than costing a refetch of the whole modal.
  const handleAttachmentsChange = (id, list) =>
    setPayments((prev) => prev.map((p) => (p.id === id ? { ...p, attachments: list } : p)));

  // A refetch can drop the payment the open panel belongs to. Leaving it up
  // would let files be uploaded against an id that is no longer here.
  useEffect(() => {
    if (attachmentsForId && !payments.some((p) => p?.id === attachmentsForId)) {
      setAttachmentsForId(null);
    }
  }, [payments, attachmentsForId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewPayment((prev) => ({ ...prev, [name]: value }));
  };

  const formatPaymentDate = (d) => {
    if (!d) return '—';
    if (typeof d === 'string') return d;
    if (d.toISOString) return d.toISOString().slice(0, 10);
    return String(d);
  };

  const toggleSortDir = () => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
  const sortedPayments = sortByDate(payments, 'payment_date', sortDir);
  // The payment the picker is open for, so the picker can grey out the bills it
  // is already linked to.
  const linkPromptPayment = sortedPayments.find((p) => p?.id === linkPromptPaymentId);
  const attachmentsPayment = sortedPayments.find((p) => p?.id === attachmentsForId);

  return (
    <ModalBackdrop onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <ModalHeader>
          <ModalTitle>Payments – {subscription?.biller}</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </ModalHeader>
        {flashMessage && <FlashMessage message={flashMessage} />}

        <form onSubmit={handleSubmit}>
          <h4 style={{ marginBottom: 12 }}>{editingId ? 'Edit payment' : 'Add payment'}</h4>
          <FormRow>
            <Label>Payment Date</Label>
            <BillDateInput
              name="payment_date"
              value={newPayment.payment_date}
              onChange={handleChange}
              required
            />
          </FormRow>
          <FormRow>
            <Label>Amount</Label>
            <BillNumberInput
              name="payment_amount"
              value={newPayment.payment_amount}
              onChange={handleChange}
              required
            />
          </FormRow>
          <FormRow>
            <Label>Charge (optional)</Label>
            <BillNumberInput
              name="payment_charge"
              value={newPayment.payment_charge}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label>Payment Method</Label>
            <input
              name="payment_method"
              value={newPayment.payment_method}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #555',
                backgroundColor: '#2e2e2e',
                color: '#e0e0e0',
              }}
            />
          </FormRow>
          <FormRow>
            <Label>Payment Type</Label>
            <select
              name="payment_type"
              value={newPayment.payment_type}
              onChange={handleChange}
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #555',
                backgroundColor: '#2e2e2e',
                color: '#e0e0e0',
              }}
            >
              <option value="Principal">Principal</option>
              <option value="Charge">Charge</option>
            </select>
          </FormRow>
          <FormRow>
            <Label>Remarks</Label>
            <input
              name="remarks"
              value={newPayment.remarks}
              onChange={handleChange}
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #555',
                backgroundColor: '#2e2e2e',
                color: '#e0e0e0',
              }}
            />
          </FormRow>
          <SubmitButton type="submit">{editingId ? 'Update Payment' : 'Add Payment'}</SubmitButton>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              style={{
                marginLeft: 12,
                padding: '8px 16px',
                background: '#555',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          )}
        </form>

        {linkPromptPaymentId && (
          <LinkPaymentPrompt
            key={linkPromptPaymentId}
            targetLabel="Bill"
            loadOptions={loadBillOptions}
            onLink={handleLinkPayment}
            onDismiss={() => setLinkPromptPaymentId(null)}
            alreadyLinkedIds={linkedParentIds(linkPromptPayment)}
          />
        )}

        <h4 style={{ marginTop: 24, marginBottom: 8 }}>Recorded payments (this subscription)</h4>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <BillsTableWrapper style={{ marginTop: 8 }}>
            <BillsTableEl>
              <thead>
                <tr>
                  <BillsTableHeader
                    onClick={toggleSortDir}
                    title="Sort by date"
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Date {sortDir === 'desc' ? '▼' : '▲'}
                  </BillsTableHeader>
                  <BillsTableHeader>Amount</BillsTableHeader>
                  <BillsTableHeader>Charge</BillsTableHeader>
                  <BillsTableHeader>Type</BillsTableHeader>
                  <BillsTableHeader>Method</BillsTableHeader>
                  <BillsTableHeader>Remarks</BillsTableHeader>
                  <BillsTableHeader>Receipts</BillsTableHeader>
                  <BillsTableHeader>Link</BillsTableHeader>
                  <BillsTableHeader>Actions</BillsTableHeader>
                </tr>
              </thead>
              <tbody>
                {sortedPayments.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: 16, color: '#888' }}>
                      No payments yet. Add one above.
                    </td>
                  </tr>
                ) : (
                  sortedPayments.map((p) => (
                    <BillsTableRow key={p.id || p.payment_date + p.payment_amount}>
                      <BillsTableData>{formatPaymentDate(p.payment_date)}</BillsTableData>
                      <BillsTableData>{p.payment_amount}</BillsTableData>
                      <BillsTableData>{p.payment_charge ?? '—'}</BillsTableData>
                      <BillsTableData>{p.payment_type || 'Principal'}</BillsTableData>
                      <BillsTableData>{p.payment_method}</BillsTableData>
                      <BillsTableData>{p.remarks || '—'}</BillsTableData>
                      <BillsTableData>
                        <button
                          type="button"
                          onClick={() =>
                            setAttachmentsForId((current) => (current === p.id ? null : p.id))
                          }
                          title={
                            attachmentsForId === p.id
                              ? 'Hide receipts'
                              : 'Add or view receipts for this payment'
                          }
                          style={{
                            ...rowBtnStyle(attachmentCount(p) > 0 ? '#c8e6c9' : '#888'),
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <FaPaperclip /> {attachmentCount(p)}
                        </button>
                      </BillsTableData>
                      <BillsTableData>
                        <PaymentLinkCell
                          links={readPaymentLinks(p)}
                          confirmingLinkId={
                            pendingUnlink?.paymentId === p.id ? pendingUnlink.linkId : null
                          }
                          busy={unlinkBusy}
                          onRequestUnlink={(linkId) =>
                            setPendingUnlink({ paymentId: p.id, linkId })
                          }
                          onConfirmUnlink={(linkId) => handleUnlink(p.id, linkId)}
                          onCancelUnlink={() => setPendingUnlink(null)}
                          onRequestLink={
                            p.id
                              ? () => {
                                  setPendingUnlink(null);
                                  setLinkPromptPaymentId(p.id);
                                }
                              : undefined
                          }
                        />
                      </BillsTableData>
                      <BillsTableData>
                        {pendingDeleteId === p.id ? (
                          <div>
                            <div style={{ fontSize: '0.8rem', marginBottom: 4 }}>
                              {deletePaymentWarning(p, 'this subscription')}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDelete(p.id)}
                              disabled={deleteBusy}
                              style={rowBtnStyle('#f44336')}
                            >
                              {deleteBusy ? 'Deleting...' : 'Confirm'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingDeleteId(null)}
                              disabled={deleteBusy}
                              style={rowBtnStyle('#bbb')}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => handleEdit(p)}
                              style={rowBtnStyle('#4caf50')}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPendingUnlink(null);
                                setPendingDeleteId(p.id);
                              }}
                              style={rowBtnStyle('#f44336')}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </BillsTableData>
                    </BillsTableRow>
                  ))
                )}
              </tbody>
            </BillsTableEl>
          </BillsTableWrapper>
        )}

        {/* Outside the form on purpose — a file input and its buttons inside it
            would sit one stray Enter key away from submitting the payment. */}
        {attachmentsPayment && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ marginBottom: 4 }}>
              Receipts for {formatPaymentDate(attachmentsPayment.payment_date)} ·{' '}
              {attachmentsPayment.payment_amount}
            </h4>
            <button
              type="button"
              onClick={() => setAttachmentsForId(null)}
              style={rowBtnStyle('#bbb')}
            >
              Hide receipts
            </button>
            <PaymentAttachments
              paymentId={attachmentsForId}
              attachments={readPaymentAttachments(attachmentsPayment)}
              onChange={(list) => handleAttachmentsChange(attachmentsForId, list)}
              onFlashMessage={setFlash}
            />
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 16,
            padding: '8px 16px',
            background: '#555',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default SubscriptionPaymentsModal;
